import React, {useState, useEffect} from "react";
import {useParams, useHistory} from "react-router-dom";
import {useMutation} from "@tanstack/react-query";
import styled from "@emotion/styled";
import {Button, Input} from "reactstrap";
import {BiArrowBack, BiCheckCircle, BiTrash} from "react-icons/bi";
import Sidebar from "components/Common/UploadEvidence/UploadEvidenceSidebar";
import ScoringGuide from "../ScoringGuide";
import {
  editCountryBenchmarkingMilestoneQuestion,
  getBenchmarkingQuestionnaire, getCountryBenchmarkingMilestone
} from "services/milestoneServices"
import RadarChart from "../RadarChart";
import {useSelector} from "react-redux";
import ConfirmModal from "components/Common/ConfirmModal";
import {milestoneOrFocalAreaAccess} from "../../../helpers/access/milestoneOrFocalAreaAccess";

// Styled Components
const PageContent = styled.div`
    background-color: #f8f9fa;
    min-height: 100vh;
    padding: 2em;
    padding-top: 5em;

    & h2 {
        margin-top: 25px;
        margin-bottom: 20px;
        color: #049046;
        font-weight: 400;
        font-size: 20px;
    }
`;

const ProgressBarContainer = styled.div`
    width: 80%;
    height: 15px;
    background-color: #e0e0e0;
    border-radius: 10px;
    margin: 20px auto;
    overflow: hidden;
`;

const ProgressBarFill = styled.div`
    height: 100%;
    background-color: ${({progress}) =>
            progress <= 50 ? "#ff4e4e" : progress <= 75 ? "#ff9900" : "#198754"};
    width: ${({progress}) => `${progress}%`};
    transition: width 0.4s ease, background-color 0.4s ease;
`;

const QuestionBlock = styled.div`
    background-color: white;
    padding: 1.5em;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    position: relative;

    & p {
        font-size: 1.1rem;
        font-weight: 400;
        margin-top: 20px;
        margin-bottom: 20px;
    }
`;

const RequiredTag = styled.span`
    position: absolute;
    top: 10px;
    left: 10px;
    background-color: #f0f0f0;
    color: #888;
    font-size: 0.8rem;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: bold;
    margin-bottom: 10px;
`;

const AnswerOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const AnswerButton = styled.button`
    background-color: ${(props) => (props.selected ? "#d4f8d4" : "#f0f0f0")};
    color: ${(props) => (props.selected ? "#198754" : "#333")};
    border: 1px solid ${(props) => (props.selected ? "#198754" : "#e0e0e0")};
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 5px;
    width: 100%;

    &:hover {
        background-color: ${(props) => (props.disabled ? "#f0f0f0" : "#d4f8d4")};
    }

    & svg {
        visibility: ${(props) => (props.selected ? "visible" : "hidden")};
    }
`;

function FillFormBenchmarking(props) {
  const {milestoneId, countryId} = useParams();
  const {user} = useSelector((state) => state.Profile);
  const currentUserId = user?.id;

  const [milestoneDetails, setMilestoneDetails] = useState({
    name: "",
    ownerId: null,
    supervisorId: null,
    isComplete: false,
    approvedBySupervisor: false,
  });

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const toggleConfirm = () => setIsConfirmOpen(!isConfirmOpen);
  const [role, setRole] = useState(null);
  const [formData, setFormData] = useState(null);
  const [responses, setResponses] = useState([]);
  const [progress, setProgress] = useState(0);
  const history = useHistory();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [evidenceData, setEvidenceData] = useState({});
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // State variables for permissions
  const [ownerId, setOwnerId] = useState(null);
  const [supervisorId, setSupervisorId] = useState(null);
  const [updateForbidden, setUpdateForbidden] = useState(false);
  const [ownerCanUpdate, setOwnerCanUpdate] = useState(false);
  const [supervisorCanUpdate, setSupervisorCanUpdate] = useState(false);
  const [isOwnerAndSupervisorSame, setIsOwnerAndSupervisorSame] = useState(false);


  const toggleSidebar = (questionId) => {
    setSelectedQuestionId(questionId);
    setSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const loadFormDetails = async () => {
      setIsLoading(true)
      try {
        const [data, milestoneData] = await Promise.all([
          getBenchmarkingQuestionnaire(countryId, milestoneId),
          getCountryBenchmarkingMilestone(countryId, milestoneId),
        ]);

        setMilestoneDetails({
          name: milestoneData?.benchmarking_milestone?.name,
          ownerId: milestoneData?.owner_and_supervisor?.owner_id,
          supervisorId: milestoneData?.owner_and_supervisor?.supervisor_id,
          isComplete: milestoneData?.is_complete,
          approvedBySupervisor: milestoneData?.approved_by_supervisor,
        });

        setFormData(data);
        setResponses(
          data?.benchmarking_milestone_questions?.map((question) => ({
            question_id: question?.question_id,
            question_response: question?.question_response || "",
            question_response_score: question?.question_response_score || 0,
            question_note: question?.question_note || "",
            question: question?.benchmarking_milestone_question?.question || "",
            // Initialize evidence data from existing question data
            evidence: question?.question_evidences?.map(evidence => ({
              name: evidence?.policy_name,
              file: evidence?.policy_evidence_file,
              // id: evidence.id // Keep track of existing evidence IDs
            })) || []
          })) || []
        );

        // Initialize evidenceData state with existing evidences
        const initialEvidenceData = {};
        data?.benchmarking_milestone_questions?.forEach(question => {
          if (question.policy_evidence_files?.length > 0) {
            initialEvidenceData[question.question_id] = question.policy_evidence_files.map(evidence => ({
              name: evidence.policy_name,
              file: evidence?.policy_evidence_file, // Existing files are already on server
              // id: evidence.id
            }));
          }
        });
        setEvidenceData(initialEvidenceData);

      } catch (err) {
        console.error("Error loading form details:", err);
      } finally {
        setIsLoading(false)
      }
    };
    loadFormDetails();
  }, [countryId, milestoneId]);

  // Separate useEffect for permission calculations
  useEffect(() => {
    if (!milestoneDetails.ownerId) return; // Avoid running when milestoneDetails is empty

    const {ownerId, supervisorId} = milestoneDetails;

    setOwnerId(ownerId);
    setSupervisorId(supervisorId);

    const isOwnerAndSupervisorSame = ownerId === supervisorId;

    const {role_, canOwnerUpdate, canSupervisorUpdate, forbiddenUpdate} =
      milestoneOrFocalAreaAccess({currentUserId, ...milestoneDetails});

    setRole(role_);
    setIsOwnerAndSupervisorSame(isOwnerAndSupervisorSame);
    setOwnerCanUpdate(canOwnerUpdate);
    setSupervisorCanUpdate(canSupervisorUpdate);
    setUpdateForbidden(forbiddenUpdate);

  }, [milestoneDetails, currentUserId]);

  useEffect(() => {
    if (responses.length > 0) {
      const totalQuestions = responses.length;
      const answeredQuestions = responses.filter(
        (res) => res.question_response !== ""
      ).length;
      const progressPercentage = Math.floor(
        (answeredQuestions / totalQuestions) * 100
      );
      setProgress(progressPercentage);
    }
  }, [responses]);

  const handleResponseChange = (question_id, answer) => {
    const score = parseInt(answer.split("]")[0].replace("[", ""), 10) || 0;

    setResponses((prevResponses) =>
      prevResponses.map((res) =>
        res.question_id === question_id
          ? {
            ...res,
            question_response: answer.split("]")[1].trim(),
            question_response_score: score,
          }
          : res
      )
    );
  };

  const handleSave = () => {
    if (updateForbidden) {
      props.alert("You do not have permission to save this milestone.", false);
      return;
    }
    saveResponses();
  };

  const onAddFile = (fileData) => {
    setEvidenceData((prevData) => {
      const updatedData = {...prevData};
      const currentEvidence = updatedData[selectedQuestionId] || [];
      updatedData[selectedQuestionId] = [...currentEvidence, fileData];
      return updatedData;
    });
  };

  const onFileRemove = (index) => {
    setEvidenceData((prevData) => {
      const updatedData = {...prevData};
      // Check if this is an existing evidence (has an ID) and mark for deletion if needed
      if (updatedData[selectedQuestionId]?.[index]?.id) {
        updatedData[selectedQuestionId][index]._delete = true;
      } else {
        // If it's a new file, just remove it
        updatedData[selectedQuestionId].splice(index, 1);
      }
      return updatedData;
    });
  };

  const {mutate: saveResponses} = useMutation({
    mutationFn: async () => {
      for (const response of responses) {
        if (response.question_response) {
          const formData = new FormData();
          const questionEvidences = evidenceData[response.question_id] || [];

          // Prepare evidence data
          const evidenceUpdateRequests = questionEvidences
            .filter(evidence => !evidence._delete) // Exclude marked for deletion
            .map(evidence => ({
              policy_name: evidence.name,
              id: evidence.id || null // null for new evidences
            }));

          // Prepare the JSON payload
          formData.append('milestone_update_request_body', JSON.stringify({
            question_update_request_body: {
              question_response: response.question_response,
              ...(response.question_note && {question_note: response.question_note}),
            },
            question_evidence_update_request_bodies: evidenceUpdateRequests,
            question_evidence_ids_to_delete: questionEvidences
              .filter(evidence => evidence._delete && evidence.id)
              .map(evidence => evidence.id)
          }));

          // Add all files for this question
          questionEvidences.forEach(evidence => {
            if (evidence.file && !evidence.id) { // Only add new files that haven't been uploaded yet
              formData.append('policy_evidence_files', evidence.file);
            }
          });

          await editCountryBenchmarkingMilestoneQuestion(
            countryId,
            milestoneId,
            response.question_id,
            formData
          );
        }
      }
    },
    onSuccess: async () => {
      props.alert("All responses saved!", true);
      history.push(`/system-forms/${countryId}/${milestoneId}`);
    },
    onError: (error) => {
      props.alert("Failed to save responses!", false);
      console.error("Error saving responses:", error);
    },
  });


  const actionText = isOwnerAndSupervisorSame && (currentUserId === ownerId && currentUserId === supervisorId)
    ? "Submit Responses"
    : ownerCanUpdate
      ? "Submit Responses for Supervisor Approval"
      : supervisorCanUpdate
        ? "Approve Submitted Responses"
        : null;

  return (
    <PageContent>
      {(!isLoading && updateForbidden && (
        <div className="alert alert-danger" role="alert" style={{marginTop: "40px", textAlign: "center"}}>
          You do not have permission to edit this milestone.
        </div>
      ))}

      <ProgressBarContainer>
        <ProgressBarFill progress={progress}/>
      </ProgressBarContainer>
      <RadarChart data={formData} responses={responses}/>
      <ScoringGuide/>

      <div className="mt-4">
        <h2>{formData?.benchmarking_milestone || "Benchmarking Form"}</h2>
      </div>

      <div className="mt-4">
        {responses.map((question, idx) => (
          <QuestionBlock key={question.question_id}>
            <RequiredTag>Required</RequiredTag>
            <p>{`${idx + 1}. ${question.question}`}</p>
            <AnswerOptions>
              {[
                "[0] Not at all",
                "[1] Policy exists but it's either outdated and/or there are implementation challenges",
                "[2] There is considerable policy implementation but still room for improvement",
                "[3] Satisfactory progress with policy implementation",
              ].map((option) => (
                <AnswerButton
                  key={option}
                  selected={
                    question.question_response === option.split("]")[1].trim()
                  }
                  onClick={() => handleResponseChange(question.question_id, option)}
                  disabled={updateForbidden}
                  style={{
                    cursor: updateForbidden ? "not-allowed" : "pointer",
                    opacity: updateForbidden ? 0.6 : 1,
                  }}
                >
                  <BiCheckCircle/>
                  {option}
                </AnswerButton>
              ))}
            </AnswerOptions>
            <Button
              color="primary"
              onClick={() => toggleSidebar(question.question_id)}
              style={{marginTop: "15px"}}
              disabled={updateForbidden}
            >
              View/Upload Evidence
            </Button>
            <Input
              type="textarea"
              placeholder="Add a note..."
              value={question.question_note || ""}
              onChange={(e) =>
                setResponses((prevResponses) =>
                  prevResponses.map((res) =>
                    res.question_id === question.question_id
                      ? {...res, question_note: e.target.value}
                      : res
                  )
                )
              }
              disabled={updateForbidden}
              style={{
                marginTop: "10px",
                cursor: updateForbidden ? "not-allowed" : "text",
                opacity: updateForbidden ? 0.6 : 1,
              }}
            />
          </QuestionBlock>
        ))}
      </div>

      <div className="flex justify-between mb-3">
        <button className="common-button" onClick={() => history.goBack()}>
          <BiArrowBack style={{marginRight: "4px"}}/> Go Back
        </button>

        {actionText && (
          <button
            className="common-button"
            onClick={toggleConfirm}
          >
            {actionText}
          </button>
        )}
      </div>

      <ConfirmModal onConfirmClick={handleSave} show={isConfirmOpen} toggle={toggleConfirm} role={role}/>

      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setSidebarOpen(false)}
        evidenceData={evidenceData[selectedQuestionId] || []}
        onAddFile={onAddFile}
        onFileRemove={onFileRemove}
        formType={'benchmarking'}
        updateForbidden={updateForbidden}
      />
    </PageContent>
  );
}

export default FillFormBenchmarking;